<template>
  <div class="flex-grow flex-shrink min-w-0 p-6 overflow-auto">
    <form v-if="!uiFlags.isFetchingItem" @submit.prevent="updateAccount">
      <div
        class="flex flex-row p-4 border-b border-slate-25 dark:border-slate-800"
      >
        <div
          class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
        >
          <h4 class="block-title text-black-900 dark:text-slate-200">
            {{ $t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE') }}
          </h4>
          <p>{{ $t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE') }}</p>
        </div>
        <div class="p-4 flex-grow-0 flex-shrink-0 flex-[50%]">
          <label :class="{ error: $v.name.$error }">
            {{ $t('GENERAL_SETTINGS.FORM.NAME.LABEL') }}
            <input
              v-model="name"
              type="text"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="$v.name.$touch"
            />
            <span v-if="$v.name.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.NAME.ERROR') }}
            </span>
          </label>
          <label :class="{ error: $v.locale.$error }">
            {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL') }}
            <select v-model="locale">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
            <span v-if="$v.locale.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR') }}
            </span>
          </label>
          <label v-if="featureInboundEmailEnabled">
            {{ $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED') }}
          </label>
          <label v-if="featureCustomReplyDomainEnabled">
            {{
              $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
            }}
          </label>
          <label v-if="featureCustomReplyDomainEnabled">
            {{ $t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL') }}
            <input
              v-model="domain"
              type="text"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
          </label>
          <label v-if="featureCustomReplyEmailEnabled">
            {{ $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL') }}
            <input
              v-model="supportEmail"
              type="text"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </label>
          <label
            v-if="showAutoResolutionConfig"
            :class="{ error: $v.autoResolveDuration.$error }"
          >
            {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.LABEL') }}
            <input
              v-model="autoResolveDuration"
              type="number"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.PLACEHOLDER')
              "
              @blur="$v.autoResolveDuration.$touch"
            />
            <span v-if="$v.autoResolveDuration.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.ERROR') }}
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              v-model="sendDailySummary"
            />
            {{ $t('GENERAL_SETTINGS.FORM.DAILY_LEAD_SUMMARY.LABEL') }}
          </label>

          <!-- New Input Field for Email (Shown Conditionally) -->
          <div v-if="sendDailySummary" class="pt-2">
            <label :class="{ error: $v.dailySummaryEmail.$error }">
              {{ $t('GENERAL_SETTINGS.FORM.DAILY_SUMMARY_EMAIL.LABEL') }}
              <input
                v-model="dailySummaryEmail"
                type="email"
                :placeholder="$t('GENERAL_SETTINGS.FORM.DAILY_SUMMARY_EMAIL.PLACEHOLDER')"
                @blur="$v.dailySummaryEmail.$touch"
              />
              <span v-if="$v.dailySummaryEmail.$error" class="message">
                <span v-if="!$v.dailySummaryEmail.required">
                  {{ $t('GENERAL_SETTINGS.FORM.DAILY_SUMMARY_EMAIL.REQUIRED') }}
                </span>
                <span v-if="!$v.dailySummaryEmail.email">
                  {{ $t('GENERAL_SETTINGS.FORM.DAILY_SUMMARY_EMAIL.EMAIL') }}
                </span>
              </span>
            </label>
          </div>
            <label class="email_notify" v-if="currentUser.role == 'administrator'">
              <p>
              {{ $t('GENERAL_SETTINGS.FORM.EMAIL_NOTIFICATION_ON_ASSIGNMENT.LABEL') }}
              </p>
              <input
                v-model="emailNotificationOnAssignment"
                type="checkbox"
              />
            </label>
        </div>
      </div>
      <div
        v-if="showAgentCommunicationScoreEmail"
        class="flex flex-row p-4 border-b border-slate-25 dark:border-slate-800"
      >
        <div
          class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
        >
          <h4 class="block-title text-black-900 dark:text-slate-200">
            {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.TITLE') }}
          </h4>
          <p>{{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.NOTE') }}</p>
        </div>
        <div class="p-4 flex-grow-0 flex-shrink-0 flex-[50%]">
          <label class="email_notify">
            <p>
              {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.ENABLED_LABEL') }}
            </p>
            <input
              v-model="agentCommunicationScoreEmailEnabled"
              type="checkbox"
            />
          </label>
          <label
            v-if="agentCommunicationScoreEmailEnabled"
            class="pt-2"
            :class="{ error: $v.agentCommunicationScoreEmailThreshold.$error }"
          >
            {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.THRESHOLD_LABEL') }}
            <input
              v-model="agentCommunicationScoreEmailThreshold"
              type="number"
              min="1"
              max="100"
              step="any"
              :placeholder="$t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.THRESHOLD_PLACEHOLDER')"
              @blur="$v.agentCommunicationScoreEmailThreshold.$touch"
            />
            <span v-if="$v.agentCommunicationScoreEmailThreshold.$error" class="message">
              <span v-if="!$v.agentCommunicationScoreEmailThreshold.required">
                {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.THRESHOLD_REQUIRED') }}
              </span>
              <span v-else>
                {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.THRESHOLD_ERROR') }}
              </span>
            </span>
          </label>
          <label
            v-if="agentCommunicationScoreEmailEnabled"
            class="pt-2"
            :class="{ error: $v.agentCommunicationScoreEmailUsers.$error }"
          >
            {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.USERS_LABEL') }}
            <multiselect
              v-model="agentCommunicationScoreEmailUsers"
              :options="adminUserOptions"
              track-by="id"
              label="name"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :hide-selected="true"
              :placeholder="$t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.USERS_PLACEHOLDER')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
              @close="$v.agentCommunicationScoreEmailUsers.$touch"
            />
            <span v-if="!adminUserOptions.length" class="text-slate-600 dark:text-slate-400 text-xs">
              {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.USERS_EMPTY') }}
            </span>
            <span v-if="$v.agentCommunicationScoreEmailUsers.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.AGENT_COMMUNICATION_SCORE_EMAIL.USERS_REQUIRED') }}
            </span>
          </label>
        </div>
      </div>
      <div
        class="p-4 border-slate-25 dark:border-slate-700 text-black-900 dark:text-slate-300 row"
      >
        <div
          class="flex-grow-0 flex-shrink-0 flex-[25%] min-w-0 py-4 pr-6 pl-0"
        >
          <h4 class="block-title text-black-900 dark:text-slate-200">
            {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.TITLE') }}
          </h4>
          <p>
            {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.NOTE') }}
          </p>
        </div>
        <div class="p-4 flex-grow-0 flex-shrink-0 flex-[50%]">
          <woot-code :script="getAccountId" />
        </div>
      </div>
      <!-- <div class="text-sm text-center p-4">
        <div>{{ `v${globalConfig.appVersion}` }}</div>
        <div v-if="hasAnUpdateAvailable && globalConfig.displayManifest">
          {{
            $t('GENERAL_SETTINGS.UPDATE_CHATWOOT', {
              latestChatwootVersion: latestChatwootVersion,
            })
          }}
        </div>
        <div class="build-id">
          <div>{{ `Build ${globalConfig.gitSha}` }}</div>
        </div>
      </div> -->

      <woot-submit-button
        class="button nice success button--fixed-top"
        :button-text="$t('GENERAL_SETTINGS.SUBMIT')"
        :loading="isUpdating"
      />
    </form>

    <woot-loading-state v-if="uiFlags.isFetchingItem" />
  </div>
</template>

<script>
import { required, minValue, maxValue, email, requiredIf } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import alertMixin from 'shared/mixins/alertMixin';
import configMixin from 'shared/mixins/configMixin';
import accountMixin from '../../../../mixins/account';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import semver from 'semver';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';

export default {
  components: {
    Multiselect,
  },
  mixins: [accountMixin, alertMixin, configMixin, uiSettingsMixin],
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
      autoResolveDuration: null,
      latestChatwootVersion: null,
      sendDailySummary: false,
      dailySummaryEmail: '',
      featureEmailNotificationOnConversationAssign:true,
      emailNotificationOnAssignment: true,
      agentCommunicationScoreEmailEnabled: false,
      agentCommunicationScoreEmailUsers: [],
      agentCommunicationScoreEmailThreshold: null,
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
    autoResolveDuration: {
      minValue: minValue(1),
      maxValue: maxValue(999),
    },
    dailySummaryEmail: {
      required: requiredIf(function() {
        return this.sendDailySummary;
      }),
      email,
    },
    agentCommunicationScoreEmailThreshold: {
      // Required when the alert toggle is on; otherwise free to be blank.
      // When provided, must be a number in the 1..100 range.
      required: requiredIf(function() {
        return this.agentCommunicationScoreEmailEnabled;
      }),
      validRange(value) {
        if (value === null || value === undefined || value === '') return true;
        const num = Number(value);
        return Number.isFinite(num) && num >= 1 && num <= 100;
      },
    },
    agentCommunicationScoreEmailUsers: {
      // At least one admin must be selected when the alert toggle is on.
      requiredWhenEnabled(value) {
        if (!this.agentCommunicationScoreEmailEnabled) return true;
        return Array.isArray(value) && value.length > 0;
      },
    },
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      agentList: 'agents/getAgents',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    showAgentCommunicationScoreEmail() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AGENT_COMMUNICATION_SCORE_EMAIL
      );
    },
    adminUserOptions() {
      return (this.agentList || [])
        .filter(user => user.role === 'administrator')
        .map(user => ({ id: user.id, name: user.name || user.email }));
    },
    hasAnUpdateAvailable() {
      if (!semver.valid(this.latestChatwootVersion)) {
        return false;
      }

      return semver.lt(
        this.globalConfig.appVersion,
        this.latestChatwootVersion
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },

    featureInboundEmailEnabled() {
      return !!this.features.inbound_emails;
    },

    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },

    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },

    getAccountId() {
      return this.id.toString();
    },
  },
  mounted() {
    if (!this.id) {
      this.initializeAccount();
    }
 },
  methods: {
      async initializeAccount() {
        try {
          await this.$store.dispatch('accounts/get');
          const account = this.getAccount(this.accountId);
          this.$root.$i18n.locale = account.locale;
          this.name = account.name;
          this.locale = account.locale;
          this.id = account.id;
          this.domain = account.domain;
          this.supportEmail = account.support_email;
          this.features = account.features;
          this.autoResolveDuration = account.auto_resolve_duration;
          this.latestChatwootVersion = account.latest_chatwoot_version;
          this.dailySummaryEmail = daily_summary_email || null;
          this.sendDailySummary = !!daily_summary_email; 
          this.emailNotificationOnAssignment= account.custom_attributes.email_notification_on_assignment;
          this.hydrateAgentCommunicationScoreEmail(account);
        } catch (error) {
          console.error('Error initializing account:', error);
        }
        // Load admin users once for the multi-select; safe to call repeatedly.
        if (this.showAgentCommunicationScoreEmail) {
          try {
            await this.$store.dispatch('agents/get');
            this.hydrateAgentCommunicationScoreEmail(this.getAccount(this.accountId));
          } catch (error) {
            console.error('Error fetching agents:', error);
          }
        }
    },

    hydrateAgentCommunicationScoreEmail(account) {
      const attrs = (account && account.custom_attributes) || {};
      this.agentCommunicationScoreEmailEnabled = !!attrs.agent_communication_score_email_enabled;
      const selectedIds = Array.isArray(attrs.agent_communication_score_email_user_ids)
        ? attrs.agent_communication_score_email_user_ids
        : [];
      this.agentCommunicationScoreEmailUsers = this.adminUserOptions.filter(u =>
        selectedIds.includes(u.id)
      );
      const storedThreshold = attrs.agent_communication_score_email_threshold;
      this.agentCommunicationScoreEmailThreshold =
        storedThreshold === null || storedThreshold === undefined || storedThreshold === ''
          ? null
          : Number(storedThreshold);
    },

    async updateAccount() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      console.log(this.emailNotificationOnAssignment);
      try {
        const payload = {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          auto_resolve_duration: this.autoResolveDuration,
          daily_summary_email: this.sendDailySummary ? this.dailySummaryEmail : null,
          email_notification_on_assignment: this.emailNotificationOnAssignment,
        };
        if (this.showAgentCommunicationScoreEmail) {
          payload.agent_communication_score_email_enabled = this.agentCommunicationScoreEmailEnabled;
          payload.agent_communication_score_email_user_ids = this.agentCommunicationScoreEmailUsers.map(u => u.id);
          // Blank => clears the value on the backend; no threshold means no email.
          payload.agent_communication_score_email_threshold =
            this.agentCommunicationScoreEmailThreshold === '' ||
            this.agentCommunicationScoreEmailThreshold === null ||
            this.agentCommunicationScoreEmailThreshold === undefined
              ? null
              : Number(this.agentCommunicationScoreEmailThreshold);
        }
        await this.$store.dispatch('accounts/update', payload);
        this.$root.$i18n.locale = this.locale;
        this.getAccount(this.id).locale = this.locale;
        this.updateDirectionView(this.locale);
        this.showAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        this.showAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },

    updateDirectionView(locale) {
      const isRTLSupported = getLanguageDirection(locale);
      this.updateUISettings({
        rtl_view: isRTLSupported,
      });
    },
  },
};
</script>
<style>
.email_notify 
{
  display:flex;
  input{
  margin: 11px 12px;;
  }
  p{
    margin-left:8px;
    margin-bottom:0px;
    font-weight: normal;
    font-size:18px;
  }
}
</style>
